import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import AnalyticsPanel from "./AnalyticsPanel";

/**
 * BroadcastPanel — surfaces listener URL + listener count + what's on-air,
 * plus the master "Record my set" controls and an inline compact
 * analytics strip. Extracted from RadioHostStudio.
 */
export default function BroadcastPanel({ slug, stationName, live, deckA, deckB, xfader, onMic, recorder }) {
  const [copied, setCopied] = useState(false);
  const [listeners, setListeners] = useState(0);
  const listenerUrl = `${window.location.origin}/radio/station/${slug}`;
  const onAirDeck = xfader < 0.5 ? "A" : "B";
  const onAirTrack = onAirDeck === "A" ? deckA : deckB;

  useEffect(() => {
    if (!live) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await api.get(`/radio/host/session/${slug}`);
        if (!cancelled) setListeners(data?.listeners || 0);
      } catch {}
    };
    tick();
    const id = setInterval(tick, 8000);
    return () => { cancelled = true; clearInterval(id); };
  }, [live, slug]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(listenerUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  const fmtDur = (s) => {
    if (!s) return "0:00";
    const m = Math.floor(s / 60); const ss = String(s % 60).padStart(2, "0");
    return `${m}:${ss}`;
  };

  return (
    <section data-testid="radio-host-broadcast" className="px-3 pt-1 pb-2 bg-[#04070B]">
      <div className="rounded-lg border border-fuchsia-500/25 bg-gradient-to-r from-fuchsia-600/10 via-purple-600/10 to-tv-accent/10 p-2 flex flex-wrap items-center gap-2">
        {/* Listener link — single compact row, no descriptive paragraph */}
        <div className="flex items-center gap-1.5 min-w-0 flex-1">
          <div className="text-[9px] uppercase tracking-widest font-black text-fuchsia-200 shrink-0">Listen</div>
          {live && (
            <span className="inline-flex items-center gap-1 bg-tv-live/20 text-red-200 border border-tv-live/40 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-wider shrink-0">
              <span className="w-1.5 h-1.5 bg-tv-live rounded-full animate-pulse" /> LIVE
            </span>
          )}
          <code data-testid="broadcast-listener-url" className="flex-1 min-w-0 bg-black/50 border border-white/10 rounded px-2 py-1 text-[10px] font-mono text-white truncate" title={listenerUrl}>
            {listenerUrl}
          </code>
          <button
            data-testid="broadcast-copy-url"
            onClick={copy}
            className="bg-fuchsia-500 hover:bg-fuchsia-400 text-black font-bold text-[10px] px-2 py-1 rounded shrink-0"
          >
            {copied ? "✓" : "Copy"}
          </button>
          <a
            data-testid="broadcast-open-station"
            href={listenerUrl}
            target="_blank" rel="noreferrer"
            className="bg-white/10 hover:bg-white/20 text-white font-bold text-[10px] px-2 py-1 rounded shrink-0"
            title="Preview what listeners see and hear"
          >
            Open ↗
          </a>
        </div>

        {/* On Air pill (compact) */}
        <div className="text-center bg-black/30 border border-white/10 rounded-lg px-2 py-1 shrink-0">
          <div className="text-[8px] uppercase tracking-widest text-white/45 font-black">On Air</div>
          <div className={`font-display font-black text-[12px] leading-tight ${onAirDeck === "A" ? "text-sky-300" : "text-rose-300"}`}>
            Deck {onAirDeck}{onMic ? " +MIC" : ""}
          </div>
          <div className="text-white/65 text-[9px] truncate max-w-[140px]">{onAirTrack?.title || "—"}</div>
        </div>

        {/* Record my set (compact) */}
        {recorder && (
          <div data-testid="broadcast-recorder" className="bg-black/30 border border-white/10 rounded-lg px-2 py-1 flex items-center gap-1.5 shrink-0">
            {!recorder.recording ? (
              <button
                data-testid="broadcast-rec-start"
                onClick={recorder.start}
                className="bg-red-500 hover:bg-red-400 text-white font-black text-[10px] px-2 py-1 rounded inline-flex items-center gap-1"
                title="Capture full live mix as WebM"
              >
                <span className="w-1.5 h-1.5 bg-white rounded-full" /> REC
              </button>
            ) : (
              <button
                data-testid="broadcast-rec-stop"
                onClick={recorder.stop}
                className="bg-white text-black font-black text-[10px] px-2 py-1 rounded inline-flex items-center gap-1 animate-pulse"
              >
                <span className="w-1.5 h-1.5 bg-red-500 rounded-sm" /> STOP · {fmtDur(recorder.durationSec)}
              </button>
            )}
            {recorder.blobUrl && !recorder.recording && (
              <a
                data-testid="broadcast-rec-download"
                href={recorder.blobUrl}
                download={recorder.suggestedFilename}
                className="text-tv-accent hover:underline font-bold text-[9px]"
              >Download ↓</a>
            )}
            {recorder.error && <div className="text-red-300 text-[8px] leading-tight max-w-[100px]">{recorder.error}</div>}
          </div>
        )}

        {/* Listeners count (compact) */}
        <div className="text-center bg-black/30 border border-white/10 rounded-lg px-2 py-1 shrink-0">
          <div className="text-[8px] uppercase tracking-widest text-white/45 font-black">Listeners</div>
          <div data-testid="broadcast-listener-count" className="font-display font-black text-base leading-none text-tv-accent">
            {live ? listeners : "—"}
          </div>
        </div>

        {/* Inline compact analytics — fills the row that the verbose
            broadcast info used to occupy. Same data as the full panel. */}
        <div className="flex-1 min-w-[280px] basis-full md:basis-auto">
          <AnalyticsPanel slug={slug} days={30} compact />
        </div>
      </div>
    </section>
  );
}
